import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  FileText, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Eye, 
  CreditCard,
  X,
  Download,
  Calendar,
  ArrowUpRight,
  Receipt,
  RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserInvoicesSection = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const token = user?.token || localStorage.getItem('token');
      const params = new URLSearchParams({ limit: '50' });
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await fetch(`${BACKEND_URL}/api/invoices/my?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async (invoiceId) => {
    setPayingId(invoiceId);
    try {
      const token = user?.token || localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/invoices/pay/${invoiceId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          success_url: `${window.location.origin}/dashboard?tab=invoices&payment=success&invoice_id=${invoiceId}`,
          cancel_url: `${window.location.origin}/dashboard?tab=invoices&payment=cancelled&invoice_id=${invoiceId}`
        })
      });
      const data = await response.json();
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else if (response.ok && data.status === 'paid') {
        // Already paid (e.g. existing Stripe invoice paid directly)
        fetchInvoices();
      } else {
        alert(data.detail || data.message || 'Payment failed');
        setPayingId(null);
      }
    } catch (err) {
      alert('Failed to initiate payment');
      setPayingId(null);
    }
  };

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const paidInvoiceId = params.get('invoice_id');
    if (paymentStatus === 'success' && paidInvoiceId) {
      // Stripe payment completed — invoice will be marked paid by the webhook
      // Poll to check if webhook has processed
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const token = user?.token || localStorage.getItem('token');
          const res = await fetch(`${BACKEND_URL}/api/invoices/my/${paidInvoiceId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const inv = await res.json();
            if (inv.status === 'paid') {
              clearInterval(pollInterval);
              fetchInvoices();
            }
          }
        } catch (e) {}
        if (attempts >= 10) clearInterval(pollInterval);
      }, 2000);
      // Clean URL
      window.history.replaceState({}, '', `${window.location.pathname}?tab=invoices`);
    } else if (paymentStatus === 'cancelled') {
      window.history.replaceState({}, '', `${window.location.pathname}?tab=invoices`);
    }
  }, []);

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'void':
        return <Badge variant="secondary">Void</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'donation': return <DollarSign className="w-4 h-4 text-emerald-400" />;
      case 'subscription': return <RefreshCw className="w-4 h-4 text-violet-400" />;
      default: return <Receipt className="w-4 h-4 text-blue-400" />;
    }
  };

  if (loading) {
    return (
      <Card className="border-border">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-invoices-section">
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Receipt className="w-5 h-5 text-emerald-400" />
                Billing & Invoices
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                View your payment history and invoices
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm bg-secondary border border-border rounded-md px-3 py-1.5 text-foreground"
                data-testid="invoice-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchInvoices}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No invoices yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Invoices will appear here when you make donations or subscribe to campaigns
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/30 transition-colors cursor-pointer"
                  onClick={() => setSelectedInvoice(invoice)}
                  data-testid={`invoice-item-${invoice.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      {getTypeIcon(invoice.type)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">
                        {invoice.description || `Invoice #${invoice.id.slice(-8)}`}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{formatDate(invoice.created_at)}</span>
                        <span className="text-xs text-muted-foreground capitalize">{invoice.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2 md:mt-0">
                    {getStatusBadge(invoice.status)}
                    <span className="text-lg font-bold text-foreground">
                      {formatCurrency(invoice.amount, invoice.currency)}
                    </span>
                    {invoice.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handlePay(invoice.id); }}
                        disabled={payingId === invoice.id}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid={`pay-invoice-${invoice.id}`}
                      >
                        {payingId === invoice.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><CreditCard className="w-4 h-4 mr-1" /> Pay</>
                        )}
                      </Button>
                    )}
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {total > invoices.length && (
            <p className="text-sm text-muted-foreground text-center mt-4">
              Showing {invoices.length} of {total} invoices
            </p>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setSelectedInvoice(null)}>
          <div
            className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="invoice-detail-modal"
          >
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <h3 className="text-lg font-semibold text-foreground">Invoice Details</h3>
              <button onClick={() => setSelectedInvoice(null)} className="p-2 rounded-lg hover:bg-secondary">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedInvoice.status)}
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}
                </p>
              </div>

              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-secondary/50">
                  <p className="text-xs text-muted-foreground">Invoice ID</p>
                  <p className="font-mono text-sm text-foreground mt-0.5">{selectedInvoice.id}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/50">
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p className="text-sm text-foreground mt-0.5">{selectedInvoice.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> Created</p>
                    <p className="text-sm text-foreground mt-0.5">{formatDate(selectedInvoice.created_at)}</p>
                  </div>
                  {selectedInvoice.paid_at && (
                    <div className="p-3 rounded-lg bg-secondary/50">
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Paid</p>
                      <p className="text-sm text-foreground mt-0.5">{formatDate(selectedInvoice.paid_at)}</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="text-sm text-foreground mt-0.5 capitalize">{selectedInvoice.type}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground">Currency</p>
                    <p className="text-sm text-foreground mt-0.5 uppercase">{selectedInvoice.currency}</p>
                  </div>
                </div>
                {selectedInvoice.campaign_title && (
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <p className="text-xs text-muted-foreground">Campaign</p>
                    <p className="text-sm text-emerald-400 mt-0.5">{selectedInvoice.campaign_title}</p>
                  </div>
                )}
              </div>

              {selectedInvoice.status === 'pending' && (
                <Button
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => { handlePay(selectedInvoice.id); setSelectedInvoice(null); }}
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Pay Now
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInvoicesSection;
